import React, { ReactNode, useEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import "@/marketing/styles/theme.css";
import Login from "./pages/Login";
import MfaVerifyPage from "./pages/MfaVerifyPage";
import MfaEnrollPage from "./pages/MfaEnrollPage";
import SsoCallbackPage from "./pages/SsoCallbackPage";
import { UploadPage } from "./routes/UploadPage";
import { SessionGuardWrapper } from "./components/SessionGuardWrapper";
import { useAuthStore } from "./state/authStore";

const Manager = lazy(() => import("./pages/engage/Manager"));
const EngageHome = lazy(() => import("./pages/engage/Home"));
const CallAudit = lazy(() => import("./pages/engage/CallAudit"));
const AgentSelfView = lazy(() => import("./pages/engage/AgentSelfView"));
const PaceHistory = lazy(() => import("./pages/engage/PaceHistory"));
const PaceHistoryDetail = lazy(
  () => import("./pages/engage/PaceHistoryDetail")
);
const MarketingIndex = lazy(() => import("./marketing/pages/Index"));
const Security = lazy(() => import("./marketing/pages/Security"));
const TermsOfService = lazy(() => import("./marketing/pages/TermsOfService"));
const Privacy = lazy(() => import("./marketing/pages/Privacy"));
const CookiePolicy = lazy(() => import("./marketing/pages/CookiePolicy"));
const AcceptableUse = lazy(() => import("./marketing/pages/AcceptableUse"));
const DataProcessing = lazy(() => import("./marketing/pages/DataProcessing"));
const PatientAccess = lazy(() => import("./marketing/pages/PatientAccess"));
const MarketingNotFound = lazy(() => import("./marketing/pages/NotFound"));

function RequireAuth({ children }: { children: ReactNode }) {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const isLoading = useAuthStore(s => s.isLoading);
  const mfaVerificationRequired = useAuthStore(s => s.mfaVerificationRequired);
  const mfaEnrollmentRequired = useAuthStore(s => s.mfaEnrollmentRequired);
  const location = useLocation();

  // Wait for checkAuth to complete before redirecting. Otherwise we redirect
  // to login on every refresh because isAuthenticated starts false.
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isAuthenticated)
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;

  // Authenticated but MFA not yet completed — redirect before showing any
  // protected content, passing the original path so MFA pages can redirect back.
  if (mfaVerificationRequired) {
    return (
      <Navigate to="/mfa/verify" state={{ from: location.pathname }} replace />
    );
  }

  if (mfaEnrollmentRequired) {
    return (
      <Navigate to="/mfa/enroll" state={{ from: location.pathname }} replace />
    );
  }

  return children;
}

export default function App() {
  const checkAuth = useAuthStore(s => s.checkAuth);

  // Validate session on mount so a page refresh restores auth state
  // from the server rather than defaulting to logged-out.
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <TooltipProvider>
      <Toaster richColors position="top-right" />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/auth/callback" element={<SsoCallbackPage />} />
          <Route path="/mfa/verify" element={<MfaVerifyPage />} />
          <Route path="/mfa/enroll" element={<MfaEnrollPage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/" element={<MarketingIndex />} />
          <Route path="/patient-access" element={<PatientAccess />} />
          <Route path="/security" element={<Security />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          <Route path="/acceptable-use" element={<AcceptableUse />} />
          <Route
            path="/data-processing-addendum"
            element={<DataProcessing />}
          />
          {/* Backend SSO redirects land on /dashboard. SsoCallbackPage validates
              the session and then navigates to the stored destination (or /engage).
              This also handles old /dashboard bookmarks gracefully. */}
          <Route path="/dashboard" element={<SsoCallbackPage />} />
          <Route
            path="/dashboard/*"
            element={<Navigate to="/engage" replace />}
          />
          <Route
            path="/engage"
            element={
              <RequireAuth>
                <SessionGuardWrapper>
                  <EngageHome />
                </SessionGuardWrapper>
              </RequireAuth>
            }
          />
          <Route
            path="/engage/manager"
            element={
              <RequireAuth>
                <SessionGuardWrapper>
                  <Manager />
                </SessionGuardWrapper>
              </RequireAuth>
            }
          />
          <Route
            path="/engage/calls/:id"
            element={
              <RequireAuth>
                <SessionGuardWrapper>
                  <CallAudit />
                </SessionGuardWrapper>
              </RequireAuth>
            }
          />
          <Route
            path="/engage/agent/:agentId"
            element={
              <RequireAuth>
                <SessionGuardWrapper>
                  <AgentSelfView />
                </SessionGuardWrapper>
              </RequireAuth>
            }
          />
          <Route
            path="/history"
            element={
              <RequireAuth>
                <SessionGuardWrapper>
                  <PaceHistory />
                </SessionGuardWrapper>
              </RequireAuth>
            }
          />
          <Route
            path="/engage/history"
            element={<Navigate to="/history" replace />}
          />
          <Route
            path="/history/:id"
            element={
              <RequireAuth>
                <SessionGuardWrapper>
                  <PaceHistoryDetail />
                </SessionGuardWrapper>
              </RequireAuth>
            }
          />
          <Route
            path="/engage/history/:id"
            element={<Navigate to="/history/:id" replace />}
          />
          <Route path="*" element={<MarketingNotFound />} />
        </Routes>
      </Suspense>
    </TooltipProvider>
  );
}
